// ─────────────────────────────────────────────────────────────────────────────
//  components/nav.js — Barre de navigation
//
//  Pourquoi un composant nav séparé ?
//  La nav est présente sur TOUTES les pages. Si on la dupliquait dans chaque
//  page, modifier un lien = modifier 8 fichiers. En isolant la nav dans son
//  propre module, on la change une fois, partout.
//
//  C'est le principe DRY (Don't Repeat Yourself) — une règle fondamentale
//  du développement logiciel.
//
//  La nav doit :
//  1. Afficher les liens de navigation
//  2. Mettre en évidence le lien actif (lié au router)
//  3. Afficher le bouton wallet (connecté/déconnecté)
//  4. Réagir aux changements d'état du wallet (store)
//  5. Être responsive (hamburger sur mobile)
// ─────────────────────────────────────────────────────────────────────────────

import { store } from '../store';
import {
  getAvailableWallets,
  connectWallet,
  disconnectWallet,
} from '../wallet.js';
import { navigate } from '../router';
import { createWalletIcon, setText } from '../utils/safeDom.js';
import { t } from '../i18n/en.js';

// Liens de navigation — ordre = ordre d'affichage
const NAV_LINKS = [
  { label: 'Gallery',      path: '/gallery',       group: 'primary' },
  { label: 'Mint',         path: '/mint',          group: 'primary' },
  { label: 'Editor',       path: '/editor',        group: 'primary' },
  { label: 'Webcam Pixel', path: '/capture',       group: 'primary' },
  { label: 'Collection',   path: '/my-collection', group: 'secondary' },
  { label: 'Time Machine', path: '/timemachine',   group: 'secondary' },
  { label: 'Deploy V2',    path: '/deploy-v2-devnet', group: 'secondary' },
  { label: 'V2 Errors',    path: '/deploy-v2-errors', group: 'secondary' },
  { label: 'About PROS',   path: '/about',         group: 'secondary' },
];

let _walletBusy = false;
let _cleanupStoreWallet = null;
let _cleanupStoreRoute = null;
let _cleanupDocumentClick = null;
let _cleanupWindowResize = null;
let _cleanupWindowKeydown = null;

// ── Rendu ─────────────────────────────────────────────────────────────────────

export function renderNav(container) {
  _cleanupStoreWallet?.();
  _cleanupStoreRoute?.();
  _cleanupDocumentClick?.();
  _cleanupWindowResize?.();
  _cleanupWindowKeydown?.();

  container.innerHTML = _buildNavHTML();
  _attachListeners(container);

  // Se met à jour quand le wallet change
  _cleanupStoreWallet = store.on('change:wallet', () => _updateWalletBtn(container));

  // Se met à jour quand la route change (lien actif + fermeture menu mobile)
  _cleanupStoreRoute = store.on('change:route', () => {
    _updateActiveLink(container);
    _closeMobileMenu(container);
    _closeWalletDropdown(container);
  });

  _updateWalletBtn(container);
  _updateActiveLink(container);
}

function _buildNavHTML() {
  const primaryLinks = NAV_LINKS.filter((link) => link.group === 'primary');
  const secondaryLinks = NAV_LINKS.filter((link) => link.group === 'secondary');
  return `
    <nav class="nav" role="navigation" aria-label="${t('nav.navAriaLabel')}">

      <!-- Logo / Accueil -->
      <a class="nav-brand" href="/" aria-label="${t('nav.logoAriaLabel')}">
        <span class="nav-logo">PROS</span>
      </a>

      <div class="nav-desktop">
        <ul class="nav-links nav-links-primary" role="list">
          ${primaryLinks.map(_navLinkHTML).join('')}
        </ul>
        <ul class="nav-links nav-links-secondary" role="list">
          ${secondaryLinks.map(_navLinkHTML).join('')}
        </ul>
      </div>

      <!-- Bouton wallet -->
      <div class="nav-wallet">
        <button
          class="btn btn-wallet"
          id="wallet-btn"
          aria-haspopup="true"
          aria-controls="wallet-dropdown"
          aria-expanded="false"
        >
          ${t('nav.connectWallet')}
        </button>
      </div>

      <!-- Hamburger mobile -->
      <button class="nav-hamburger" aria-label="${t('nav.menuAriaLabel')}" aria-expanded="false">
        <span></span><span></span><span></span>
      </button>

      <!-- Menu mobile (visible quand hamburger actif) -->
      <div class="nav-mobile-menu" aria-hidden="true">
        <div class="nav-mobile-group">
          <p class="nav-mobile-label pixel-text">Product</p>
          <ul role="list">
            ${primaryLinks.map(_navLinkHTML).join('')}
          </ul>
        </div>
        <div class="nav-mobile-group">
          <p class="nav-mobile-label pixel-text">Archive / About</p>
          <ul role="list">
            ${secondaryLinks.map(_navLinkHTML).join('')}
          </ul>
        </div>
      </div>

      <!-- Dropdown wallet (wallets disponibles ou actions) -->
      <div class="wallet-dropdown" id="wallet-dropdown" hidden>
      </div>
    </nav>
  `;
}

function _navLinkHTML(link) {
  return `
    <li>
      <a class="nav-link" href="${link.path}" data-path="${link.path}">
        ${link.label}
      </a>
    </li>
  `;
}

// ── Event listeners ───────────────────────────────────────────────────────────

function _attachListeners(container) {
  // Bouton wallet → ouvre le dropdown
  const walletBtn = container.querySelector('#wallet-btn');
  walletBtn.addEventListener('click', () => _toggleWalletDropdown(container));

  // Un clic sur un lien mobile ferme le menu immédiatement (navigation plus stable)
  container.querySelectorAll('.nav-mobile-menu .nav-link').forEach((link) => {
    link.addEventListener('click', () => _closeMobileMenu(container));
  });

  // Hamburger mobile
  const hamburger = container.querySelector('.nav-hamburger');
  const mobileMenu = container.querySelector('.nav-mobile-menu');
  if (mobileMenu) mobileMenu.inert = true;
  hamburger.addEventListener('click', () => {
    const expanded = hamburger.getAttribute('aria-expanded') === 'true';
    hamburger.setAttribute('aria-expanded', String(!expanded));
    mobileMenu.setAttribute('aria-hidden', String(expanded));
    hamburger.classList.toggle('is-open');
    mobileMenu.classList.toggle('is-open');
    mobileMenu.inert = expanded;
  });

  // Ferme le dropdown en cliquant ailleurs
  const onDocumentClick = (e) => {
    if (!container.contains(e.target)) {
      _closeMobileMenu(container);
      _closeWalletDropdown(container);
    }
  };
  document.addEventListener('click', onDocumentClick);
  _cleanupDocumentClick = () => document.removeEventListener('click', onDocumentClick);

  const onWindowResize = () => {
    if (window.innerWidth > 980) _closeMobileMenu(container);
  };
  window.addEventListener('resize', onWindowResize, { passive: true });
  _cleanupWindowResize = () => window.removeEventListener('resize', onWindowResize);

  const onWindowKeydown = (e) => {
    if (e.key !== 'Escape') return;
    const dropdown = container.querySelector('#wallet-dropdown');
    if (!dropdown || dropdown.hidden) return;
    e.preventDefault();
    e.stopPropagation();
    _closeWalletDropdown(container);
  };
  window.addEventListener('keydown', onWindowKeydown);
  _cleanupWindowKeydown = () => window.removeEventListener('keydown', onWindowKeydown);
}

function _closeMobileMenu(container) {
  const hamburger = container.querySelector('.nav-hamburger');
  const mobileMenu = container.querySelector('.nav-mobile-menu');
  if (!hamburger || !mobileMenu) return;
  if (mobileMenu.contains(document.activeElement)) {
    document.activeElement?.blur?.();
  }
  hamburger.setAttribute('aria-expanded', 'false');
  mobileMenu.setAttribute('aria-hidden', 'true');
  hamburger.classList.remove('is-open');
  mobileMenu.classList.remove('is-open');
  mobileMenu.inert = true;
}

function _setWalletBusy(container, busy, label = null) {
  _walletBusy = busy;
  const walletBtn = container.querySelector('#wallet-btn');
  if (walletBtn) {
    walletBtn.disabled = busy;
    walletBtn.classList.toggle('is-loading', busy);
    if (busy && label) walletBtn.textContent = label;
  }

  container.querySelectorAll('.wallet-option, #disconnect-btn').forEach((btn) => {
    btn.disabled = busy;
  });
}

function _setWalletError(container, message) {
  const dropdown = container.querySelector('#wallet-dropdown');
  if (!dropdown) return;
  const existing = dropdown.querySelector('.wallet-error');
  if (existing) existing.remove();

  const p = document.createElement('p');
  p.className = 'wallet-error pixel-text';
  p.textContent = message;
  dropdown.appendChild(p);
}

// ── Dropdown wallet ───────────────────────────────────────────────────────────

function _toggleWalletDropdown(container) {
  if (_walletBusy) return;
  const walletState = store.get('wallet');
  const dropdown = container.querySelector('#wallet-dropdown');

  if (dropdown.hidden) {
    _renderWalletDropdown(container, walletState);
    dropdown.hidden = false;
    _setWalletDropdownExpanded(container, true);
    _focusFirstInteractiveDropdownItem(container);
  } else {
    _closeWalletDropdown(container);
  }
}

function _closeWalletDropdown(container) {
  const dropdown = container.querySelector('#wallet-dropdown');
  if (!dropdown || dropdown.hidden) return;
  dropdown.hidden = true;
  _setWalletDropdownExpanded(container, false);
  _focusWalletButton(container);
}

function _setWalletDropdownExpanded(container, expanded) {
  const walletBtn = container.querySelector('#wallet-btn');
  if (!walletBtn) return;
  walletBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function _focusWalletButton(container) {
  const walletBtn = container.querySelector('#wallet-btn');
  if (walletBtn instanceof HTMLElement) walletBtn.focus();
}

function _focusFirstInteractiveDropdownItem(container) {
  const dropdown = container.querySelector('#wallet-dropdown');
  if (!dropdown || dropdown.hidden) return;
  const firstInteractive = dropdown.querySelector(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  if (firstInteractive instanceof HTMLElement) firstInteractive.focus();
}

function _renderWalletDropdown(container, walletState) {
  const dropdown = container.querySelector('#wallet-dropdown');
  if (!dropdown) return;
  dropdown.replaceChildren();

  if (walletState.connected) {
    // Wallet connecté → afficher l'adresse + bouton déconnexion
    const short = `${walletState.publicKey.slice(0, 4)}…${walletState.publicKey.slice(-4)}`;
    const walletInfo = document.createElement('div');
    walletInfo.className = 'wallet-info';

    const icon = createWalletIcon({
      src: walletState.icon,
      alt: walletState.name,
      width: 20,
      height: 20,
    });
    if (icon) walletInfo.append(icon);

    const name = document.createElement('span');
    name.className = 'wallet-name';
    setText(name, walletState.name);
    walletInfo.append(name);

    const address = document.createElement('code');
    address.className = 'wallet-address';
    setText(address, short);
    walletInfo.append(address);

    const walletMeta = document.createElement('p');
    walletMeta.className = 'wallet-meta';
    setText(walletMeta, 'Connected wallet');

    const disconnectBtn = document.createElement('button');
    disconnectBtn.className = 'btn btn-disconnect';
    disconnectBtn.id = 'disconnect-btn';
    setText(disconnectBtn, t('nav.disconnect'));

    dropdown.append(walletInfo, walletMeta, disconnectBtn);

    disconnectBtn.addEventListener('click', async () => {
      try {
        _setWalletBusy(container, true, t('nav.disconnecting'));
        await disconnectWallet();
        _closeWalletDropdown(container);
      } catch (err) {
        _setWalletError(container, t('nav.disconnectFailed'));
        console.error('[Nav] Wallet disconnect error:', err);
      } finally {
        _setWalletBusy(container, false);
        _updateWalletBtn(container);
        _focusWalletButton(container);
      }
    });

  } else {
    // Wallet déconnecté → liste des wallets disponibles
    const wallets = getAvailableWallets();

    if (!wallets.length) {
      const empty = document.createElement('p');
      empty.className = 'wallet-none';
      setText(empty, t('nav.noWalletDetected'));

      empty.append(document.createElement('br'));

      const phantom = document.createElement('a');
      phantom.setAttribute('href', 'https://phantom.app');
      phantom.setAttribute('target', '_blank');
      phantom.setAttribute('rel', 'noopener');
      setText(phantom, t('nav.installPhantom'));
      empty.append(phantom);

      dropdown.append(empty);
      return;
    }

    const choose = document.createElement('p');
    choose.className = 'wallet-choose';
    setText(choose, t('nav.chooseWallet'));

    const list = document.createElement('ul');
    list.className = 'wallet-list';
    list.setAttribute('role', 'list');

    wallets.forEach((wallet, i) => {
      const item = document.createElement('li');
      const option = document.createElement('button');
      option.className = 'wallet-option';
      option.dataset.walletIndex = String(i);

      const icon = createWalletIcon({
        src: wallet.icon,
        alt: wallet.name,
        width: 20,
        height: 20,
      });
      if (icon) option.append(icon);

      option.append(document.createTextNode(' '));
      const walletName = document.createTextNode(String(wallet.name ?? ''));
      option.append(walletName);
      item.append(option);
      list.append(item);
    });

    dropdown.append(choose, list);

    // Connexion au clic sur un wallet
    dropdown.querySelectorAll('.wallet-option').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const idx = Number(btn.dataset.walletIndex);
        try {
          _setWalletBusy(container, true, t('nav.connecting'));
          await connectWallet(wallets[idx]);
          _closeWalletDropdown(container);
        } catch (err) {
          _setWalletError(container, t('nav.connectionFailed'));
          console.error('[Nav] Wallet connection error:', err);
        } finally {
          _setWalletBusy(container, false);
          _updateWalletBtn(container);
          _focusWalletButton(container);
        }
      });
    });
  }
}

// ── Mise à jour réactive du bouton wallet ─────────────────────────────────────

function _updateWalletBtn(container) {
  const walletState = store.get('wallet');
  const btn = container.querySelector('#wallet-btn');
  if (!btn) return;

  btn.disabled = _walletBusy;
  btn.setAttribute('aria-busy', _walletBusy ? 'true' : 'false');

  if (walletState.connected) {
    const short = `${walletState.publicKey.slice(0, 4)}…${walletState.publicKey.slice(-4)}`;
    btn.replaceChildren();
    const infoWrap = document.createElement('span');
    infoWrap.className = 'btn-wallet-copy';
    const icon = createWalletIcon({
      src: walletState.icon,
      alt: '',
      width: 16,
      height: 16,
    });
    if (icon) {
      btn.append(icon);
    }
    const label = document.createElement('span');
    label.className = 'btn-wallet-name';
    setText(label, walletState.name || 'Wallet');
    const address = document.createElement('span');
    address.className = 'btn-wallet-address';
    setText(address, short);
    infoWrap.append(label, address);
    btn.append(infoWrap);
    btn.classList.add('is-connected');
  } else {
    setText(btn, t('nav.connectWallet'));
    btn.classList.remove('is-connected');
  }
}

// ── Mise à jour du lien actif ─────────────────────────────────────────────────

function _updateActiveLink(container) {
  const route = store.get('route');
  if (!route) return;

  container.querySelectorAll('.nav-link').forEach((link) => {
    const isActive = link.dataset.path === route.path;
    link.classList.toggle('is-active', isActive);
    link.setAttribute('aria-current', isActive ? 'page' : 'false');
  });
}
